import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Star, Clock, Truck, CheckCircle, ChevronRight, ChevronDown, ChevronUp, MapPin, Pencil, MessageCircle, X, BellOff, Bell, Sparkles, Wrench, Heart, Check } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppAuth } from '../hooks/useAppAuth';
import { useJobHub } from '../hooks/useSignalR';
import { jobService, quoteService, messagesApi, notificationsApi, createApiClient, profileService, vehiclesService, carrierEmployeesService, jobAssignmentService, contractService, platformFeeConfigService } from '../services/api';
import { MessagingPanel } from '../components/MessagingPanel';
import { markJobViewed } from '../components/NotificationBell';
import { RelayMap, RouteMap, LiveTrackingMap } from '../components/RelayMap';
import { JobLiveTrafficWeather } from '../components/JobLiveTrafficWeather';
import { WorkshopQuoteMapView, WorkshopQuotesPanelHeader } from '../components/WorkshopQuoteMapView';
import { JobType, JobStatus, PaymentTerm, DropStatus, PickupStatus, routeZoneLabels } from '../types';
import { DisputeCategory, DisputeParty, DisputeStatus, DisputeResolutionType, type DisputeDto } from '../types/modules';
import { PodSubmissionModal } from '../components/pod/PodSubmissionModal';
import { PocSubmissionModal } from '../components/pod/PocSubmissionModal';
import { DeliveryOtpPanel } from '../components/pod/DeliveryOtpPanel';
import { ReviewsPanel } from '../components/reviews/ReviewsPanel';
import type { JobDetailDto, QuoteSummaryDto, UserProfileDto } from '../types';
import type { LiveCarrierLocation } from '../hooks/useSignalR';
import { QuoteStatus } from '../types';

// 
//  JOB DETAIL + QUOTES PAGE
// 
export function JobDetailPage() {
  const { id: jobId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getToken, account } = useAppAuth();
  const [job, setJob] = useState<JobDetailDto | null>(null);
  const [accepting, setAccepting] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPodModal, setShowPodModal] = useState(false);
  const [podSubmitted, setPodSubmitted] = useState(false);
  const [showPocModal, setShowPocModal] = useState(false);
  const [pocSubmitted, setPocSubmitted] = useState(false);
  const [collectingPickupId, setCollectingPickupId] = useState<string | null>(null);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeCategory, setDisputeCategory] = useState<DisputeCategory>(DisputeCategory.Other);
  const [disputeTitle, setDisputeTitle] = useState('');
  const [disputeDescription, setDisputeDescription] = useState('Dispute raised regarding this job.');
  const [disputeSubmitting, setDisputeSubmitting] = useState(false);
  const [disputeSubmitted, setDisputeSubmitted] = useState(false);
  const [disputeError, setDisputeError] = useState<string | null>(null);
  const [adminResolutionType, setAdminResolutionType] = useState<number>(DisputeResolutionType.PartialRefundToCustomer);
  const [adminRefundAmount, setAdminRefundAmount] = useState('');
  const [adminResolutionNotes, setAdminResolutionNotes] = useState('');
  const [adminDisputeSubmitting, setAdminDisputeSubmitting] = useState(false);
  const [adminDisputeError, setAdminDisputeError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<{
    title: string; description: string; isSameDay: boolean;
    scheduledAt: string; goodsDescription: string;
    estimatedValueGbp: string; requiredVehicleType: number;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [editMsg, setEditMsg] = useState('');
  const [quoteSort, setQuoteSort] = useState<'price-asc' | 'price-desc' | 'rating-desc' | 'jobs-desc' | 'newest'>('price-asc');
  const [bidAmount, setBidAmount] = useState('');
  const [bidLabour, setBidLabour] = useState('');
  const [bidParts, setBidParts] = useState('');
  const [bidMessage, setBidMessage] = useState('');
  const [bidSubmitting, setBidSubmitting] = useState(false);
  const [bidError, setBidError] = useState<string | null>(null);
  const [bidSuccess, setBidSuccess] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [aiDescImproving, setAiDescImproving] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentResult, setPaymentResult] = useState<'success' | 'cancelled' | null>(null);
  const [photoWarning, setPhotoWarning] = useState(false);
  const [driverAccepting, setDriverAccepting] = useState(false);
  const [driverAcceptDone, setDriverAcceptDone] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [assignedVehicleId, setAssignedVehicleId] = useState<string>('');
  const [assignedEmployeeId, setAssignedEmployeeId] = useState<string>('');
  const [editingBid, setEditingBid] = useState(false);
  const [editBidAmount, setEditBidAmount] = useState('');
  const [repairCommissionPercent, setRepairCommissionPercent] = useState<number | null>(null);

  useEffect(() => {
    platformFeeConfigService(createApiClient(getToken)).getEffective()
      .then(cfg => setRepairCommissionPercent(cfg.repairJobCommissionPercent))
      .catch(() => {});
  }, [getToken]);
  const [editBidLabour, setEditBidLabour] = useState('');
  const [editBidParts, setEditBidParts] = useState('');
  const [editBidMessage, setEditBidMessage] = useState('');
  const [editBidSubmitting, setEditBidSubmitting] = useState(false);
  const [editBidError, setEditBidError] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [bidChatOpen, setBidChatOpen] = useState(false);
  const [jobActionBusy, setJobActionBusy] = useState(false);
  const [jobActionError, setJobActionError] = useState<string | null>(null);
  const [carrierLocation, setCarrierLocation]   = useState<LiveCarrierLocation | null>(null);
  const [showReassign, setShowReassign]         = useState(false);
  const [reassignVehicleId, setReassignVehicleId] = useState('');
  const [reassignEmployeeId, setReassignEmployeeId] = useState('');
  const [reassignError, setReassignError]       = useState<string | null>(null);
  const [dropActionBusy, setDropActionBusy]     = useState<string | null>(null);
  const [pickupActionBusy, setPickupActionBusy] = useState<string | null>(null);

  const qc = useQueryClient();
  const { data: profile } = useQuery<UserProfileDto>({
    queryKey: ['profile'],
    queryFn: () => profileService(createApiClient(getToken)).getProfile(),
    enabled: !!account,
    staleTime: Infinity,
  });
  const currentUserId = profile?.userId ?? '';

  const isCustomer  = profile?.role === 'Customer';
  const isCarrier   = profile?.role === 'Carrier';
  const isWorkshop  = profile?.role === 'Workshop';
  const isAdmin     = profile?.role === 'Admin' || profile?.role === 'Ops';

  const { data: jobDispute, refetch: refetchDispute } = useQuery<DisputeDto | null>({
    queryKey: ['job-dispute', jobId],
    queryFn: async () => {
      try {
        const client = createApiClient(getToken);
        const r = await client.get<DisputeDto | null>(`/api/disputes/job/${jobId}`);
        return r.data ?? null;
      } catch (e: any) {
        // 404 = no dispute raised yet for this job — expected, non-error state.
        // Anything else (401/500/network) must propagate so React Query retries instead of
        // permanently caching "no dispute" for staleTime and hiding the admin/customer panels.
        if (e?.response?.status === 404) return null;
        throw e;
      }
    },
    enabled: !!jobId && !!(isAdmin || isCustomer),
    staleTime: 30_000,
  });

  const { data: fleetVehicles = [] } = useQuery({
    queryKey: ['carrier-fleet'],
    queryFn: () => vehiclesService(createApiClient(getToken)).getAll(),
    enabled: !!account && isCarrier,
    staleTime: 60_000,
  });

  const { data: carrierEmployees = [] } = useQuery({
    queryKey: ['carrier-employees'],
    queryFn: () => carrierEmployeesService(createApiClient(getToken)).getAll(),
    enabled: !!account && isCarrier,
    staleTime: 60_000,
  });

  const isCarrierAccepted = isCarrier && job != null &&
    (job.status === JobStatus.CarrierAccepted || job.status === JobStatus.ContractGenerated);

  const { data: currentAssignment, refetch: refetchAssignment } = useQuery({
    queryKey: ['job-assignment', jobId],
    queryFn: () => jobAssignmentService(createApiClient(getToken)).get(jobId!),
    enabled: !!jobId && isCarrierAccepted,
    staleTime: 30_000,
  });

  // Fetched separately (not via job.contract — the job-detail endpoint never populates that
  // field) through the party-restricted /api/contracts/job/:id endpoint. Resolves to null on
  // 403/404 (no contract yet, or not a party), which is the normal pre-contract state.
  const { data: contract, refetch: refetchContract } = useQuery({
    queryKey: ['contract', jobId],
    queryFn: () => contractService(createApiClient(getToken)).getContract(jobId!),
    enabled: !!jobId,
    staleTime: 30_000,
  });

  const [downloadingContract, setDownloadingContract] = useState(false);
  const handleDownloadContract = async () => {
    if (!contract) return;
    setDownloadingContract(true);
    try {
      const blobUrl = await contractService(createApiClient(getToken)).downloadPdf(contract.id);
      window.open(blobUrl, '_blank');
    } catch (e: any) {
      alert(e.message ?? 'Failed to download contract PDF.');
    } finally {
      setDownloadingContract(false);
    }
  };

  const reassignMutation = useMutation({
    mutationFn: (data: { vehicleId: string; employeeId?: string }) =>
      jobAssignmentService(createApiClient(getToken)).assign(jobId!, data),
    onSuccess: () => {
      setShowReassign(false);
      setReassignError(null);
      refetchAssignment();
      refetchContract(); // contract PDF is regenerated server-side on reassignment — pick up the new version
    },
    onError: (e: any) => setReassignError(e?.response?.data?.detail ?? e?.response?.data?.error ?? e?.message ?? 'Failed to update assignment.'),
  });

  // Derive active vehicles; always default to the first active vehicle.
  const activeVehicles = fleetVehicles.filter((v: { isActive: boolean }) => v.isActive);
  const vehicleIdToUse = assignedVehicleId || (activeVehicles[0]?.id ?? '');

  // Auto-seed state so the select reflects the default on first render.
  useEffect(() => {
    if (activeVehicles.length > 0 && !assignedVehicleId) {
      setAssignedVehicleId(activeVehicles[0].id);
    }
  }, [activeVehicles.length, activeVehicles[0]?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Default employee to owner (the carrier themselves)
  const ownerEmployee = carrierEmployees.find((e: { isOwner: boolean }) => e.isOwner);
  useEffect(() => {
    if (ownerEmployee && !assignedEmployeeId) {
      setAssignedEmployeeId(ownerEmployee.id);
    }
  }, [ownerEmployee?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Quote panel view mode — list or map (map only available on repair jobs for customers)
  const [quotesView, setQuotesView] = useState<'list' | 'map'>('list');
  // Ref map for scrolling to a quote card when its map marker is clicked
  const quoteCardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!jobId || !account) return;
    createApiClient(getToken).get(`/api/jobs/${jobId}`)
      .then(r => { setJob(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    // Opening a job detail dismisses pending voice-alert reminders for this job.
    markJobViewed(jobId);
    if (isCarrier) jobService(createApiClient(getToken)).attendAlerts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, account, isCarrier]);

  const { data: isMuted = false } = useQuery<boolean>({
    queryKey: ['job-mute', jobId],
    queryFn: async () => {
      const token = await getToken();
      return notificationsApi.isJobMuted(jobId!, token);
    },
    enabled: !!jobId && isCarrier,
    staleTime: 60_000,
  });

  const muteMutation = useMutation({
    mutationFn: async (mute: boolean) => {
      const token = await getToken();
      return mute
        ? notificationsApi.muteJob(jobId!, token)
        : notificationsApi.unmuteJob(jobId!, token);
    },
    onSuccess: (_data, mute) => {
      qc.setQueryData(['job-mute', jobId], mute);
    },
  });

  useEffect(() => {
    if (!jobId || !isCustomer) return;
    getToken().then(token =>
      messagesApi.getUnreadPerCarrier(jobId, token)
        .then(items => {
          const map: Record<string, number> = {};
          items.forEach(({ carrierId, unreadCount }) => { map[carrierId] = unreadCount; });
          setUnreadCounts(map);
        })
        .catch(() => {})
    );
  }, [jobId, isCustomer, getToken]);

  const refreshJob = () => {
    if (!jobId) return;
    createApiClient(getToken).get(`/api/jobs/${jobId}`)
      .then(r => setJob(r.data))
      .catch(() => {});
  };

  useJobHub({
    getToken,
    enabled: !!account,
    jobId,
    onQuoteReceived: refreshJob,
    onQuoteAccepted: refreshJob,
    onContractGenerated: refreshJob,
    onPaymentReleased:   refreshJob,
    onCarrierAcceptedJob: refreshJob,
    onCarrierLocationUpdated: (loc) => setCarrierLocation(loc),
    onJobStarted:        refreshJob,
    onJobCompleted:      refreshJob,
    onJobPaused:         refreshJob,
    onJobResumed:        refreshJob,
    onJobPodSubmitted:   refreshJob,
  });

  // Poll every 30 s as a fallback for state changes not covered by push events.
  useEffect(() => {
    if (!jobId) return;
    const id = setInterval(() => {
      createApiClient(getToken).get(`/api/jobs/${jobId}`)
        .then(r => setJob(r.data))
        .catch(() => {});
    }, 30_000);
    return () => clearInterval(id);
  }, [jobId]);

  const handleAcceptQuote = async (quoteId: string) => {
    const isCreditTerm = (job?.paymentTerm ?? PaymentTerm.Cash) === PaymentTerm.Credit;
    const confirmMsg = isCreditTerm
      ? 'Accept this quote? The driver will review and accept. Your contract will be issued on credit terms  payment is due after delivery.'
      : 'Accept this quote? You\'ll be prompted to pay, and the driver will also need to sign the agreement before your contract is issued.';
    if (!job || !window.confirm(confirmMsg)) return;
    setAccepting(quoteId);
    setError(null);
    try {
      const client = createApiClient(getToken);
      await client.post('/api/v1/quotes/accept', { quoteId, jobId: job.id });
      setAccepted(true);
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
    } catch (err: any) {
      setError(err.response?.data?.detail ?? err.response?.data?.error ?? 'Failed to accept quote.');
    } finally {
      setAccepting(null);
    }
  };

  const handleRejectQuote = async (quoteId: string) => {
    if (!job || !window.confirm('Decline this quote?')) return;
    setRejecting(quoteId);
    setError(null);
    try {
      const client = createApiClient(getToken);
      await client.post(`/api/v1/quotes/${quoteId}/reject`, {});
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
    } catch (err: any) {
      setError(err.response?.data?.detail ?? err.response?.data?.error ?? 'Failed to decline quote.');
    } finally {
      setRejecting(null);
    }
  };

  const handlePublishDraft = async () => {
    if (!jobId || !window.confirm('Submit this repair job for workshop quotes? It will go live immediately.')) return;
    setPublishing(true);
    setPublishError(null);
    try {
      const client = createApiClient(getToken);
      await jobService(client).publishDraft(jobId);
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
    } catch (err: any) {
      setPublishError(err.response?.data?.detail ?? err.response?.data?.error ?? 'Failed to publish job.');
    } finally {
      setPublishing(false);
    }
  };

  const handleCancelJob = async () => {
    if (!jobId || !window.confirm('Are you sure you want to cancel this job? This cannot be undone.')) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const client = createApiClient(getToken);
      await jobService(client).cancelJob(jobId, 'Cancelled by customer');
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
    } catch (err: any) {
      setCancelError(err.response?.data?.detail ?? err.response?.data?.error ?? 'Failed to cancel job.');
    } finally {
      setCancelling(false);
    }
  };

  // GPS tracking (LiveTrackingMap) stays up while Paused too — carrier location just stops
  // updating; the map still shows the last known position rather than falling back to a static
  // route mid-job.
  const isLiveTrackedStatus = job !== null && (job.status === JobStatus.InProgress || job.status === JobStatus.Paused);
  const preStartStatuses = new Set([
    JobStatus.Draft, JobStatus.PendingReview, JobStatus.Active, JobStatus.Quoted,
    JobStatus.Accepted, JobStatus.ContractGenerated, JobStatus.CarrierAccepted,
  ]);
  const isLiveRoute = job !== null && job.jobType !== JobType.VehicleRepair &&
    !(job.isRelayJob && job.relaySegments.length > 0) &&
    (isLiveTrackedStatus || (job.jobType === JobType.SameDayDelivery && preStartStatuses.has(job.status)));

  const isDraftRepairCarrier = isCarrier && job !== null && job.status === JobStatus.Draft && job.jobType === JobType.VehicleRepair;
  const isDraftCustomer = isCustomer && job !== null && job.status === JobStatus.Draft;
  const canCancel = isCustomer && job !== null && [
    JobStatus.PendingReview, JobStatus.Active, JobStatus.Quoted,
    JobStatus.Accepted, JobStatus.CarrierAccepted,
  ].includes(job.status);
  const canEdit = isCustomer && job !== null && [0, 1, 2, 3].includes(job.status);
  const isBidder = isCarrier || isWorkshop;
  const myExistingQuote = isBidder && job ? job.quotes.find(q => q.carrierId === currentUserId) : undefined;
  const isRepairJob = job?.jobType === JobType.VehicleRepair;
  const canBid = isBidder && job !== null && (job.status === 2 || job.status === 3) && !myExistingQuote;

  const isInternalJob = job?.isInternal === true;

  // An internal job never had a bidding round, so there is no accepted quote to derive
  // ownership from. The carrier who posted it IS the carrier doing it - Jobs.CarrierId was
  // set to them at creation - and that is what every carrier-side control below keys off.
  const isInternalOwner = isInternalJob && !isCustomer && job !== null &&
    job.carrierId?.toLowerCase() === currentUserId?.toLowerCase();

  // True if the currently-logged-in driver is the one whose quote was accepted.
  // Defensive coercions: production JSON may deliver status as string; carrierId comparison
  // is case-insensitive to guard against any UUID casing differences between endpoints.
  const isAssignedDriver = isInternalOwner || (!isCustomer && job !== null &&
    job.quotes.some(q =>
      q.carrierId?.toLowerCase() === currentUserId?.toLowerCase() &&
      Number(q.status) === QuoteStatus.Accepted
    ));
  // Driver may chat during open bidding (2-3) or if they are the assigned driver post-confirmation
  const driverCanChat = !isCustomer && job !== null &&
    ((job.status >= 2 && job.status <= 3) || isAssignedDriver);


  // Carrier GPS broadcasting — web browser equivalent of the mobile locationTracking service.
  // Watches the browser geolocation and posts to the carrier location endpoint every ~30 s
  // while the assigned carrier has this job in progress.
  useEffect(() => {
    if (!isAssignedDriver || !jobId || job?.status !== JobStatus.InProgress) return;
    if (!navigator?.geolocation) return;

    let lastSentAt = 0;
    const MIN_INTERVAL_MS = 25_000;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - lastSentAt < MIN_INTERVAL_MS) return;
        lastSentAt = now;
        createApiClient(getToken).post(`/api/jobs/${jobId}/location`, {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }).catch(() => {});
      },
      () => {}, // GPS errors are non-fatal
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 },
    );

    return () => navigator.geolocation.clearWatch(watchId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssignedDriver, jobId, job?.status]);

  //  Payment / flow state 
  // isJobClosed: contract generated (status >=5)  job locked, banner for others
  // isAcceptedOrBeyond: customer accepted a quote (status >=4)  show payment/acceptance panel
  const isCash             = (job?.paymentTerm ?? PaymentTerm.Cash) === PaymentTerm.Cash;
  const isCredit           = (job?.paymentTerm ?? PaymentTerm.Cash) === PaymentTerm.Credit;
  const isJobClosed        = (job?.status ?? 0) >= 5;
  const isAcceptedOrBeyond = (job?.status ?? 0) >= 4;
  const isTerminal         = job?.status === 9 || job?.status === 10 || job?.status === 11;
  const acceptedQuote      = job?.quotes.find(q => Number(q.status) === QuoteStatus.Accepted);
  const showCustomerPay    = isCustomer && isAcceptedOrBeyond && !isTerminal && paymentResult !== 'success' && (
    (isCash   && job?.status === JobStatus.Accepted && !!job?.carrierAcceptedAt) ||
    (isCredit && (job?.status === JobStatus.CarrierAccepted || ((job?.status ?? 0) >= 6 && (job?.status ?? 0) <= 8)))
  );
  const showWaitingForDriver = isCustomer && isCash && (job?.status === JobStatus.Accepted || job?.status === JobStatus.ContractGenerated) && !job?.carrierAcceptedAt;
  const showDriverPayFee     = isAssignedDriver && isCredit && (job?.status ?? 0) >= 7 && (job?.status ?? 0) < 9;
  // Backend allows carrier-accept at Accepted (4) or ContractGenerated (5); show button at both.
  const showDriverAccept     = isAssignedDriver && (job?.status === JobStatus.Accepted || job?.status === JobStatus.ContractGenerated) && !job?.carrierAcceptedAt && !driverAcceptDone;
  const showDriverAccepted   = isAssignedDriver && (!!job?.carrierAcceptedAt || driverAcceptDone);

  // Reviews open up as soon as a quote is accepted and stay open for the rest of
  // the job lifecycle — mirrors the backend's non-reviewable status list.
  const nonReviewableStatuses = new Set([
    JobStatus.Draft, JobStatus.PendingReview, JobStatus.Active, JobStatus.Quoted,
    JobStatus.Cancelled, JobStatus.Rejected,
  ]);
  const canReview = (isCustomer || isAssignedDriver) && job !== null && !nonReviewableStatuses.has(job.status);
  const reviewCounterpartyId   = isCustomer ? (acceptedQuote?.carrierId ?? '') : (job?.customerId ?? '');
  const reviewCounterpartyName = isCustomer ? (acceptedQuote?.carrierName ?? (isRepairJob ? 'Workshop' : 'Carrier')) : (job?.customerName ?? 'Customer');
  const reviewRevieweeLabel    = isCustomer ? (isRepairJob ? 'Workshop' : 'Carrier') : 'Customer';

  //  Driver Accept diagnostics (remove after fix)
  if (!isCustomer && job) {
    console.log('[DriverAccept] diagnostics', {
      currentUserId,
      role: profile?.role,
      isCustomer,
      jobStatus: job.status,
      carrierAcceptedAt: job.carrierAcceptedAt,
      driverAcceptDone,
      isAssignedDriver,
      showDriverAccept,
      quotes: job.quotes.map(q => ({ carrierId: q.carrierId, status: q.status, statusType: typeof q.status })),
      QuoteStatusAccepted: QuoteStatus.Accepted,
      quotesMatchingCurrentUser: job.quotes.filter(q => q.carrierId === currentUserId),
      quotesWithAcceptedStatus: job.quotes.filter(q => q.status === QuoteStatus.Accepted),
    });
  }

  const handleOpenEdit = () => {
    if (!job) return;
    setEditForm({
      title: job.title,
      description: job.description,
      isSameDay: job.isSameDay,
      scheduledAt: new Date(job.scheduledAt).toISOString().slice(0, 16),
      goodsDescription: job.goodsDescription,
      estimatedValueGbp: '',
      requiredVehicleType: job.requiredVehicleType,
    });
    setEditing(true);
    setEditMsg('');
  };

  const handleSaveEdit = async () => {
    if (!editForm || !jobId) return;
    setSaving(true);
    setEditMsg('');
    try {
      const client = createApiClient(getToken);
      await client.put(`/api/jobs/${jobId}`, {
        title: editForm.title,
        description: editForm.description,
        isSameDay: editForm.isSameDay,
        scheduledAt: new Date(editForm.scheduledAt).toISOString(),
        goodsDescription: editForm.goodsDescription,
        estimatedValueGbp: editForm.estimatedValueGbp ? parseFloat(editForm.estimatedValueGbp) : null,
        requiredVehicleType: Number(editForm.requiredVehicleType),
      });
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
      setEditing(false);
      setEditMsg('Job updated successfully.');
    } catch (err: any) {
      setEditMsg(err.response?.data?.detail ?? err.response?.data?.error ?? 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitBid = async () => {
    if (!jobId) return;
    if (isWorkshop && (!bidLabour || !bidParts)) return;
    if (!isWorkshop && !bidAmount) return;
    setBidSubmitting(true);
    setBidError(null);
    try {
      const client = createApiClient(getToken);
      await quoteService(client).submitQuote(
        isWorkshop
          ? { jobId, amount: 0, labourAmount: parseFloat(bidLabour), partsAmount: parseFloat(bidParts), message: bidMessage.trim() || undefined }
          : { jobId, amount: parseFloat(bidAmount), message: bidMessage.trim() || undefined }
      );
      // Submission succeeded — clear form immediately so the carrier can't re-submit.
      setBidAmount('');
      setBidLabour('');
      setBidParts('');
      setBidMessage('');
      setBidSuccess(true);
      // Refresh job to populate myExistingQuote and hide the submit form.
      try {
        const refreshed = await client.get(`/api/jobs/${jobId}`);
        setJob(refreshed.data);
      } catch {
        // Refresh failure is non-fatal; the 15-second poller will pick it up.
      }
    } catch (err: any) {
      setBidError(err.response?.data?.detail ?? err.response?.data?.error ?? 'Failed to submit bid.');
    } finally {
      setBidSubmitting(false);
    }
  };

  const handleOpenEditBid = () => {
    if (!myExistingQuote) return;
    if (myExistingQuote.labourAmount != null) {
      setEditBidLabour(myExistingQuote.labourAmount.toFixed(2));
      setEditBidParts((myExistingQuote.partsAmount ?? 0).toFixed(2));
    } else {
      setEditBidAmount(myExistingQuote.amount.toFixed(2));
    }
    setEditBidMessage(myExistingQuote.message ?? '');
    setEditBidError(null);
    setEditingBid(true);
  };

  const handleSaveEditBid = async () => {
    if (!myExistingQuote) return;
    const isWorkshopQuote = myExistingQuote.labourAmount != null;
    if (isWorkshopQuote && (!editBidLabour || !editBidParts)) return;
    if (!isWorkshopQuote && !editBidAmount) return;
    setEditBidSubmitting(true);
    setEditBidError(null);
    try {
      const client = createApiClient(getToken);
      await client.put(`/api/v1/quotes/${myExistingQuote.id}`,
        isWorkshopQuote
          ? { amount: 0, labourAmount: parseFloat(editBidLabour), partsAmount: parseFloat(editBidParts), message: editBidMessage.trim() || null }
          : { amount: parseFloat(editBidAmount), message: editBidMessage.trim() || null }
      );
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
      setEditingBid(false);
    } catch (err: any) {
      setEditBidError(err.response?.data?.detail ?? err.response?.data?.error ?? 'Failed to update bid.');
    } finally {
      setEditBidSubmitting(false);
    }
  };

  const handleWithdrawBid = async () => {
    if (!myExistingQuote) return;
    if (!window.confirm('Withdraw your bid? This cannot be undone.')) return;
    setWithdrawing(true);
    try {
      const client = createApiClient(getToken);
      await client.delete(`/api/v1/quotes/${myExistingQuote.id}`);
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
      setBidChatOpen(false);
    } catch (err: any) {
      setEditBidError(err.response?.data?.detail ?? err.response?.data?.error ?? 'Failed to withdraw bid.');
    } finally {
      setWithdrawing(false);
    }
  };

  // Read ?payment= query param set by Stripe redirect and clear it from the URL.
  useEffect(() => {
    const result = searchParams.get('payment');
    if (result === 'success' || result === 'cancelled') {
      setPaymentResult(result);
      setSearchParams(prev => { prev.delete('payment'); prev.delete('party'); return prev; }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Read ?warning=photos set when job photo upload failed during job creation.
  useEffect(() => {
    if (searchParams.get('warning') === 'photos') {
      setPhotoWarning(true);
      setSearchParams(prev => { prev.delete('warning'); return prev; }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleAiImproveDescription = async () => {
    if (!editForm?.description.trim()) return;
    setAiDescImproving(true);
    try {
      const client = createApiClient(getToken);
      const { data } = await client.post<{ description: string }>('/api/jobs/structure-description', { description: editForm.description });
      setEditForm(f => f ? { ...f, description: data.description } : f);
    } catch {
      // Silently fail  user can edit manually
    } finally {
      setAiDescImproving(false);
    }
  };

  const handleCustomerPay = async () => {
    if (!jobId) return;
    setPaymentLoading(true);
    setPaymentError(null);
    try {
      const client = createApiClient(getToken);
      const url = await jobService(client).createCustomerCheckoutUrl(jobId);
      window.location.href = url;
    } catch (err: any) {
      setPaymentError(err.message ?? 'Failed to create payment link. Please try again.');
      setPaymentLoading(false);
    }
  };

  const handleDriverPayFee = async () => {
    if (!jobId) return;
    setPaymentLoading(true);
    setPaymentError(null);
    try {
      const client = createApiClient(getToken);
      const url = await jobService(client).createCarrierCheckoutUrl(jobId);
      window.location.href = url;
    } catch (err: any) {
      setPaymentError(err.message ?? 'Failed to create platform fee payment link.');
      setPaymentLoading(false);
    }
  };

  const handleDriverAccept = async () => {
    if (!jobId || (!vehicleIdToUse && !isWorkshop)) return;
    setDriverAccepting(true);
    setPaymentError(null);
    try {
      const client = createApiClient(getToken);
      const empId = assignedEmployeeId || undefined;
      await jobService(client).carrierAcceptJob(jobId, isWorkshop ? undefined : vehicleIdToUse, empId);
      setDriverAcceptDone(true);
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
    } catch (err: any) {
      setPaymentError(err.message ?? 'Failed to accept job.');
    } finally {
      setDriverAccepting(false);
    }
  };

  const handleStartJob = async () => {
    if (!jobId) return;
    setJobActionBusy(true);
    setJobActionError(null);
    try {
      const client = createApiClient(getToken);
      await jobService(client).startJob(jobId);
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
    } catch (err: any) {
      setJobActionError(err.response?.data?.detail ?? err.response?.data?.error ?? err.message ?? 'Failed to start job.');
    } finally {
      setJobActionBusy(false);
    }
  };

  const handleCompleteJob = async () => {
    if (!jobId) return;
    setJobActionBusy(true);
    setJobActionError(null);
    try {
      const client = createApiClient(getToken);
      await jobService(client).completeJob(jobId);
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
      if (isRepairJob) setShowPodModal(true);
    } catch (err: any) {
      setJobActionError(err.response?.data?.detail ?? err.response?.data?.error ?? err.message ?? 'Failed to mark job as delivered.');
    } finally {
      setJobActionBusy(false);
    }
  };

  const handlePauseJob = async () => {
    if (!jobId) return;
    setJobActionBusy(true);
    setJobActionError(null);
    try {
      const client = createApiClient(getToken);
      await client.post(`/api/jobs/${jobId}/pause`);
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
    } catch (err: any) {
      setJobActionError(err.response?.data?.detail ?? err.response?.data?.error ?? err.message ?? 'Failed to pause job.');
    } finally {
      setJobActionBusy(false);
    }
  };

  const handleResumeJob = async () => {
    if (!jobId) return;
    setJobActionBusy(true);
    setJobActionError(null);
    try {
      const client = createApiClient(getToken);
      await client.post(`/api/jobs/${jobId}/resume`);
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
    } catch (err: any) {
      setJobActionError(err.response?.data?.detail ?? err.response?.data?.error ?? err.message ?? 'Failed to resume job.');
    } finally {
      setJobActionBusy(false);
    }
  };

  const handleArriveAtDrop = async (dropId: string) => {
    if (!jobId) return;
    setDropActionBusy(dropId);
    try {
      const client = createApiClient(getToken);
      await client.post(`/api/v1/jobs/${jobId}/drops/${dropId}/arrive`);
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
    } catch { /* non-fatal */ } finally {
      setDropActionBusy(null);
    }
  };

  const handleCompleteDrop = async (dropId: string) => {
    if (!jobId) return;
    setDropActionBusy(dropId);
    try {
      const client = createApiClient(getToken);
      await client.post(`/api/v1/jobs/${jobId}/drops/${dropId}/complete`, {
        photoUrls: [], latitude: 0, longitude: 0,
      });
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
    } catch { /* non-fatal */ } finally {
      setDropActionBusy(null);
    }
  };

  const handleArriveAtPickup = async (pickupId: string) => {
    if (!jobId) return;
    setPickupActionBusy(pickupId);
    try {
      const client = createApiClient(getToken);
      await client.post(`/api/v1/jobs/${jobId}/pickups/${pickupId}/arrive`);
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
    } catch { /* non-fatal */ } finally {
      setPickupActionBusy(null);
    }
  };

  const handleCollectPickup = (pickupId: string) => {
    setCollectingPickupId(pickupId);
  };

  const refreshAfterPickupCollected = async () => {
    if (!jobId) return;
    setCollectingPickupId(null);
    try {
      const client = createApiClient(getToken);
      const refreshed = await client.get(`/api/jobs/${jobId}`);
      setJob(refreshed.data);
    } catch { /* non-fatal */ }
  };

  const statusLabels: Record<number, string> = {
    0: 'Draft', 1: 'Under Review', 2: 'Live', 3: 'Receiving Bids',
    4: 'Accepted', 5: 'Contract Sent', 6: 'In Progress',
    7: 'Completed', 8: 'POD Verified', 9: 'Paid', 10: 'Reviewed',
    11: 'Cancelled', 12: 'Rejected', 13: 'Driver Accepted', 14: 'Paused',
  };

  const jobTypeLabels: Record<number, string> = {
    0: 'Same-Day Delivery', 1: 'Scheduled Delivery', 2: 'Relay Delivery',
    3: 'Vehicle Repair', 4: 'Removal', 5: 'Freight / Haulage',
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
    </div>
  );

  if (!job) return (
    <div className="page-shell text-center text-gray-500">
      Job not found.
    </div>
  );

  return (
    <div className="page-shell">
      {/* Job Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs font-mono text-gray-400">{job.referenceNumber}</span>
              {job.isSameDay && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                   Same-Day
                </span>
              )}
              {job.isRelayJob && (
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                  "„ Relay
                </span>
              )}
              {job.customerEditedAt && (
                <span className="text-xs bg-blue-50 text-blue-600 border border-blue-200 px-2 py-0.5 rounded-full font-medium">
                   Edited
                </span>
              )}
              {isInternalJob && (
                <span
                  data-testid="internal-job-badge"
                  className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-medium"
                >
                  Internal Fleet Job
                </span>
              )}
            </div>
            <h1 className="text-xl font-bold text-gray-900">{job.title}</h1>
            <p className="text-sm text-gray-500 mt-1">{jobTypeLabels[job.jobType]}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canEdit && !editing && (
              <button
                onClick={handleOpenEdit}
                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <Pencil className="w-3 h-3" /> Edit
              </button>
            )}
            <span className={`text-sm px-3 py-1 rounded-full font-medium ${
              job.status === 2 ? 'bg-green-100 text-green-700' :
              job.status === 3 ? 'bg-blue-100 text-blue-700' :
              job.status >= 4 ? 'bg-gray-100 text-gray-600' :
              'bg-amber-100 text-amber-700'
            }`}>
              {statusLabels[job.status] ?? 'Unknown'}
            </span>
          </div>
        </div>

        <p className="text-sm text-gray-700 mb-4">{job.description}</p>

        {/* Photo upload failure warning */}
        {photoWarning && (
          <div className="mb-4 flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
            <span className="shrink-0">⚠️</span>
            <span>Your job was posted successfully, but the photos could not be saved. You can try uploading them again.</span>
            <button onClick={() => setPhotoWarning(false)} className="ml-auto shrink-0 text-amber-600 hover:text-amber-800">✕</button>
          </div>
        )}

        {/* Goods images / videos */}
        {job.goodsImageUrls && job.goodsImageUrls.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Goods Photos / Videos</p>
            <div className="grid grid-cols-3 gap-2">
              {job.goodsImageUrls.map((url, i) => {
                const isVideo = /\.(mp4|mov|webm|avi)(\?|$)/i.test(url);
                return isVideo ? (
                  <video
                    key={i}
                    src={url}
                    controls
                    className="w-full rounded-lg border border-gray-200 bg-black"
                    style={{ aspectRatio: '4/3', objectFit: 'cover' }}
                  />
                ) : (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                    <img
                      src={url}
                      alt={`Goods ${i + 1}`}
                      className="w-full rounded-lg border border-gray-200 object-cover hover:opacity-90 transition-opacity cursor-zoom-in"
                      style={{ aspectRatio: '4/3' }}
                    />
                  </a>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 text-sm">
          {isRepairJob ? (
            <div className="col-span-2">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Vehicle Location</p>
                  <p className="text-gray-700">{job.pickupAddress}</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Pickup</p>
                    <p className="text-gray-700">{job.pickupAddress}</p>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Delivery</p>
                    <p className="text-gray-700">{job.deliveryAddress}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-6 text-xs text-gray-500">
          <span><Truck className="inline w-3 h-3 mr-1" />{job.distanceMiles.toFixed(1)} miles</span>
          <span><Clock className="inline w-3 h-3 mr-1" />
            {new Date(job.scheduledAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>

        {/* Route charging zones — shown to every role. Route information, not a quote
            breakdown: it explains why a London run costs more without itemising anyone's
            price. Zone names are withheld server-side on hidden-address jobs. */}
        {routeZoneLabels(job.routeZoneCodes).length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="job-route-zones">
            <span className="text-[11px] text-gray-400 mr-0.5">Route passes through</span>
            {routeZoneLabels(job.routeZoneCodes).map(label => (
              <span
                key={label}
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
              >
                🛣️ {label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Draft job — customer publish action */}
      {isDraftCustomer && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-blue-800 flex items-center gap-2">
                <Clock className="w-4 h-4 shrink-0" /> Saved Draft
              </p>
              <p className="text-sm text-blue-700 mt-1">
                This job is saved as a draft. Use the Edit button to make any changes, then submit it when you're ready to receive quotes.
              </p>
              {publishError && <p className="text-sm text-red-600 mt-2">{publishError}</p>}
            </div>
            <button
              onClick={handlePublishDraft}
              disabled={publishing}
              className="shrink-0 flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 transition-colors"
            >
              {publishing ? 'Submitting…' : '▶ Submit for Quotes'}
            </button>
          </div>
        </div>
      )}

      {/* Draft repair job — carrier publish action */}
      {isDraftRepairCarrier && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-800 flex items-center gap-2">
                <Wrench className="w-4 h-4 shrink-0" /> Employee-Drafted Repair Job
              </p>
              <p className="text-sm text-amber-700 mt-1">
                This repair job was created by one of your employees. Review the details above, then submit it to receive workshop quotes.
              </p>
              {publishError && <p className="text-sm text-red-600 mt-2">{publishError}</p>}
            </div>
            <button
              onClick={handlePublishDraft}
              disabled={publishing}
              className="shrink-0 flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg disabled:opacity-50 transition-colors"
            >
              {publishing ? 'Submitting…' : '▶ Submit for Quotes'}
            </button>
          </div>
        </div>
      )}

      {/* Cancel Job — customer action for pre-contract statuses */}
      {canCancel && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-red-800 flex items-center gap-2">
                <X className="w-4 h-4 shrink-0" /> Cancel This Job
              </p>
              <p className="text-sm text-red-700 mt-1">
                You can cancel this job before a contract is issued.
              </p>
              {cancelError && <p className="text-sm text-red-600 mt-2">{cancelError}</p>}
            </div>
            <button
              onClick={handleCancelJob}
              disabled={cancelling}
              className="shrink-0 flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 transition-colors"
            >
              {cancelling ? 'Cancelling…' : 'Cancel Job'}
            </button>
          </div>
        </div>
      )}

      {/*  Route / Live Tracking map  */}
      {job.isRelayJob && job.relaySegments.length > 0 ? (
        <div className="mb-6">
          <RelayMap
            segments={job.relaySegments}
            pickupLat={job.pickupLat} pickupLng={job.pickupLng}
            deliveryLat={job.deliveryLat} deliveryLng={job.deliveryLng}
          />
        </div>
      ) : isLiveTrackedStatus && job.jobType !== JobType.VehicleRepair ? (
        <div className="mb-6">
          <LiveTrackingMap
            pickupLat={job.pickupLat} pickupLng={job.pickupLng} pickupAddress={job.pickupAddress}
            deliveryLat={job.deliveryLat} deliveryLng={job.deliveryLng} deliveryAddress={job.deliveryAddress}
            carrierLocation={carrierLocation}
          />
        </div>
      ) : (
        <div className="mb-6">
          <RouteMap
            pickupLat={job.pickupLat} pickupLng={job.pickupLng} pickupAddress={job.pickupAddress}
            deliveryLat={job.deliveryLat} deliveryLng={job.deliveryLng} deliveryAddress={job.deliveryAddress}
            live={job.jobType === JobType.SameDayDelivery}
          />
        </div>
      )}

      {/*  Traffic + weather along the live route — only while the map above is actually live
          (in-progress/paused GPS tracking, or a pre-start Same Day Delivery live route).
          Hidden for relay jobs, Vehicle Repair, and any static/scheduled route. */}
      {isLiveRoute && (
        <JobLiveTrafficWeather
          pickupLat={job.pickupLat} pickupLng={job.pickupLng}
          deliveryLat={job.deliveryLat} deliveryLng={job.deliveryLng}
          weatherLat={carrierLocation?.lat ?? job.pickupLat}
          weatherLng={carrierLocation?.lng ?? job.pickupLng}
        />
      )}

      {/*  Confirmed-job banner (non-customer, contract generated)  */}
      {isJobClosed && !isCustomer && (
        <div className={`mb-4 px-4 py-3 rounded-xl text-sm border flex items-start gap-3 ${
          isAssignedDriver
            ? 'bg-indigo-50 border-indigo-200 text-indigo-800'
            : 'bg-gray-50 border-gray-200 text-gray-600'
        }`}>
          <span className="text-lg leading-none mt-0.5">
            {isAssignedDriver ? '✅' : '🔒'}
          </span>
          <div>
            {isAssignedDriver ? (
              <>
                <p className="font-semibold text-indigo-900">You are the assigned driver</p>
                <p className="mt-0.5">This job is confirmed. Use the chat below to stay in contact with the customer.</p>
              </>
            ) : (
              <>
                <p className="font-semibold text-gray-700">This job is closed for new bids</p>
                <p className="mt-0.5">A quote has already been accepted. Only the assigned driver and customer can continue on this job.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/*  Status-4 pending note  shown to non-assigned drivers while awaiting driver confirmation */}
      {!isJobClosed && isAcceptedOrBeyond && !isCustomer && !isAssignedDriver && (
        <div className="mb-4 px-4 py-3 rounded-xl text-sm border flex items-start gap-3 bg-amber-50 border-amber-200 text-amber-800">
          <span className="text-lg leading-none mt-0.5"></span>
          <div>
            <p className="font-semibold">Quote under review</p>
            <p className="mt-0.5">A quote has been selected and is pending final confirmation. Bidding is paused.</p>
          </div>
        </div>
      )}

      {/* Edit message (success / error)  shown after form is closed */}
      {editMsg && !editing && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm border ${
          editMsg.startsWith('Job updated')
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {editMsg}
        </div>
      )}

      {/* Inline edit form  shown when customer clicks Edit */}
      {editing && editForm && (
        <div className="bg-white border border-blue-200 rounded-xl p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">Edit Job Details</h2>

          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Title
              </label>
              <input
                type="text"
                value={editForm.title}
                onChange={e => setEditForm(f => f ? { ...f, title: e.target.value } : f)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            {/* Description */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Description
                </label>
                {editForm.description.trim() && (
                  <button
                    type="button"
                    onClick={handleAiImproveDescription}
                    disabled={aiDescImproving}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-60 text-xs font-semibold transition-colors"
                  >
                    {aiDescImproving
                      ? <span className="w-3 h-3 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                      : <Sparkles className="w-3 h-3" />
                    }
                    {aiDescImproving ? 'Improving' : 'AI Improve'}
                  </button>
                )}
              </div>
              <textarea
                rows={3}
                value={editForm.description}
                onChange={e => setEditForm(f => f ? { ...f, description: e.target.value } : f)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
              />
            </div>

            {/* Goods Description */}
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Goods Description
              </label>
              <textarea
                rows={2}
                value={editForm.goodsDescription}
                onChange={e => setEditForm(f => f ? { ...f, goodsDescription: e.target.value } : f)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Scheduled At */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Scheduled At
                </label>
                <input
                  type="datetime-local"
                  value={editForm.scheduledAt}
                  onChange={e => setEditForm(f => f ? { ...f, scheduledAt: e.target.value } : f)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* Estimated Value */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Estimated Value (£)
                </label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder="Optional"
                  value={editForm.estimatedValueGbp}
                  onChange={e => setEditForm(f => f ? { ...f, estimatedValueGbp: e.target.value } : f)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Required Vehicle Type */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Required Vehicle
                </label>
                <select
                  value={editForm.requiredVehicleType}
                  onChange={e => setEditForm(f => f ? { ...f, requiredVehicleType: Number(e.target.value) } : f)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                >
                  <option value={0}>Car</option>
                  <option value={1}>Motorbike</option>
                  <option value={2}>Van</option>
                  <option value={3}>LGV</option>
                  <option value={4}>HGV</option>
                </select>
              </div>

              {/* Same-Day toggle */}
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={editForm.isSameDay}
                    onChange={e => setEditForm(f => f ? { ...f, isSameDay: e.target.checked } : f)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-400"
                  />
                  <span className="text-sm text-gray-700">Same-Day Delivery</span>
                </label>
              </div>
            </div>
          </div>

          {/* Error while saving */}
          {editMsg && (
            <div className="mt-4 px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {editMsg}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100">
            <button
              onClick={handleSaveEdit}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              {saving && (
                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {saving ? 'Saving' : 'Save Changes'}
            </button>
            <button
              onClick={() => { setEditing(false); setEditMsg(''); }}
              disabled={saving}
              className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Relay Segments */}
      {job.isRelayJob && job.relaySegments.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
          <h2 className="font-semibold text-purple-800 mb-3">
            "„ Relay Segments ({job.relaySegments.length})
          </h2>
          <div className="space-y-2">
            {job.relaySegments.map(seg => (
              <div key={seg.id} className="bg-white rounded-lg p-3 border border-purple-100 flex items-center justify-between">
                <div className="text-sm">
                  <span className="font-medium text-purple-700">Segment {seg.segmentOrder}</span>
                  <span className="text-gray-500 ml-2">{seg.distanceMiles.toFixed(1)} miles</span>
                  <span className="text-gray-400 ml-2">£{seg.relayFeePerMile}/mile</span>
                </div>
                <div className="flex items-center gap-2">
                  {seg.assignedCarrierName
                    ? <span className="text-xs text-green-600 font-medium">" {seg.assignedCarrierName}</span>
                    : <span className="text-xs text-amber-600">Awaiting driver</span>
                  }
                  <span className="text-xs font-bold text-purple-700">£{seg.totalRelayFee.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Multi-pickup stops — MultiPickupDropDelivery (type 7) */}
      {job.jobType === JobType.MultiPickupDropDelivery && job.pickups && job.pickups.length > 0 && (() => {
        const activePickupIdx = job.pickups!.findIndex(p => p.status === PickupStatus.EnRoute || p.status === PickupStatus.Arrived);
        const pickupStatusLabel = (s: PickupStatus) =>
          s === PickupStatus.Pending ? 'Pending' : s === PickupStatus.EnRoute ? 'En Route' :
          s === PickupStatus.Arrived ? 'Arrived' : s === PickupStatus.Collected ? 'Collected' :
          s === PickupStatus.Failed ? 'Failed' : 'Skipped';
        const pickupStatusColor = (s: PickupStatus) =>
          s === PickupStatus.Collected ? 'bg-emerald-100 text-emerald-700' :
          s === PickupStatus.Arrived ? 'bg-blue-100 text-blue-700' :
          s === PickupStatus.EnRoute ? 'bg-indigo-100 text-indigo-700' :
          s === PickupStatus.Failed ? 'bg-red-100 text-red-700' :
          s === PickupStatus.Skipped ? 'bg-gray-100 text-gray-500' : 'bg-amber-100 text-amber-700';
        return (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-blue-800">Pickup Stops ({job.pickups!.length})</h2>
              {isAssignedDriver && job.status === JobStatus.InProgress && activePickupIdx >= 0 && (
                <span className="text-xs font-medium text-blue-700 bg-blue-100 px-2.5 py-1 rounded-full">
                  Pickup {activePickupIdx + 1} of {job.pickups!.length}
                </span>
              )}
            </div>
            <div className="space-y-2">
              {job.pickups!.map((pickup, idx) => {
                const isActive = pickup.status === PickupStatus.EnRoute || pickup.status === PickupStatus.Arrived;
                return (
                  <div key={pickup.id} className="bg-white rounded-lg p-3 border border-blue-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-blue-700 text-sm">Pickup {idx + 1}</span>
                        <span className="text-gray-400 text-xs ml-2">{pickup.city || pickup.postCode}</span>
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${pickupStatusColor(pickup.status)}`}>
                        {pickupStatusLabel(pickup.status)}
                      </span>
                    </div>
                    {isAssignedDriver && job.status === JobStatus.InProgress && isActive && (
                      <div className="flex gap-2 mt-2">
                        {pickup.status === PickupStatus.EnRoute && (
                          <button
                            onClick={() => handleArriveAtPickup(pickup.id)}
                            disabled={pickupActionBusy === pickup.id}
                            className="text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
                          >
                            {pickupActionBusy === pickup.id ? 'Updating…' : 'Arrive'}
                          </button>
                        )}
                        {pickup.status === PickupStatus.Arrived && (
                          <button
                            onClick={() => handleCollectPickup(pickup.id)}
                            disabled={pickupActionBusy === pickup.id}
                            className="text-xs bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
                          >
                            {pickupActionBusy === pickup.id ? 'Updating…' : 'Collect'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Multi-drop stops — MultiDropDelivery (type 6) and MultiPickupDropDelivery (type 7) */}
      {(job.jobType === JobType.MultiDropDelivery || job.jobType === JobType.MultiPickupDropDelivery) && job.drops && job.drops.length > 0 && (() => {
        const activeDropIdx = job.drops!.findIndex(d => d.status === DropStatus.EnRoute || d.status === DropStatus.Arrived);
        const dropStatusLabel = (s: DropStatus) =>
          s === DropStatus.Pending ? 'Pending' : s === DropStatus.EnRoute ? 'En Route' :
          s === DropStatus.Arrived ? 'Arrived' : s === DropStatus.Completed ? 'Completed' :
          s === DropStatus.Failed ? 'Failed' : 'Skipped';
        const dropStatusColor = (s: DropStatus) =>
          s === DropStatus.Completed ? 'bg-emerald-100 text-emerald-700' :
          s === DropStatus.Arrived ? 'bg-blue-100 text-blue-700' :
          s === DropStatus.EnRoute ? 'bg-indigo-100 text-indigo-700' :
          s === DropStatus.Failed ? 'bg-red-100 text-red-700' :
          s === DropStatus.Skipped ? 'bg-gray-100 text-gray-500' : 'bg-amber-100 text-amber-700';
        return (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-indigo-800">Drop Stops ({job.drops!.length})</h2>
              {isAssignedDriver && job.status === JobStatus.InProgress && activeDropIdx >= 0 && (
                <span className="text-xs font-medium text-indigo-700 bg-indigo-100 px-2.5 py-1 rounded-full">
                  Drop {activeDropIdx + 1} of {job.drops!.length}
                </span>
              )}
            </div>
            <div className="space-y-2">
              {job.drops!.map((drop, idx) => {
                const isActive = drop.status === DropStatus.EnRoute || drop.status === DropStatus.Arrived;
                return (
                  <div key={drop.id} className="bg-white rounded-lg p-3 border border-indigo-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-indigo-700 text-sm">Drop {idx + 1}</span>
                        <span className="text-gray-400 text-xs ml-2">{drop.city || drop.postCode}</span>
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${dropStatusColor(drop.status)}`}>
                        {dropStatusLabel(drop.status)}
                      </span>
                    </div>
                    {isAssignedDriver && job.status === JobStatus.InProgress && isActive && (
                      <div className="flex gap-2 mt-2">
                        {drop.status === DropStatus.EnRoute && (
                          <button
                            onClick={() => handleArriveAtDrop(drop.id)}
                            disabled={dropActionBusy === drop.id}
                            className="text-xs bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
                          >
                            {dropActionBusy === drop.id ? 'Updating…' : 'Arrive'}
                          </button>
                        )}
                        {drop.status === DropStatus.Arrived && (
                          <button
                            onClick={() => handleCompleteDrop(drop.id)}
                            disabled={dropActionBusy === drop.id}
                            className="text-xs bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
                          >
                            {dropActionBusy === drop.id ? 'Updating…' : 'Complete'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Carrier: My Existing Bid — rendered as a standard QuoteCard */}
      {isBidder && myExistingQuote && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-6">
          <QuoteCard
            quote={myExistingQuote}
            isLowest={false}
            isCustomer={false}
            canAccept={false}
            accepting={false}
            onAccept={() => {}}
            jobId={jobId ?? ''}
            jobReference={job?.referenceNumber ?? ''}
            currentUserId={currentUserId}
            getToken={getToken}
            chatEnabled={!!account}
            unreadCount={0}
            isMyBid
            isMuted={isMuted}
            onEditBid={myExistingQuote.status === QuoteStatus.Pending ? handleOpenEditBid : undefined}
            editingBid={editingBid}
            editBidContent={editingBid ? (
              <div className="space-y-3">
                {myExistingQuote.labourAmount != null ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Labour inc. VAT (£)</label>
                      <input type="number" min={0} step={0.01} value={editBidLabour}
                        onChange={e => setEditBidLabour(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Parts inc. VAT (£)</label>
                      <input type="number" min={0} step={0.01} value={editBidParts}
                        onChange={e => setEditBidParts(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white" />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Your Price (£)</label>
                    <input type="number" min={1} step={0.01} value={editBidAmount}
                      onChange={e => setEditBidAmount(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white" />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Message (optional)</label>
                  <textarea rows={2} value={editBidMessage} onChange={e => setEditBidMessage(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none bg-white" />
                </div>
                {editBidError && <p className="text-xs text-red-600">{editBidError}</p>}
                <div className="flex gap-2">
                  <button onClick={handleSaveEditBid}
                    disabled={editBidSubmitting || (myExistingQuote.labourAmount != null ? !editBidLabour || !editBidParts : !editBidAmount)}
                    className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5">
                    {editBidSubmitting && <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    {editBidSubmitting ? 'Saving…' : 'Save Changes'}
                  </button>
                  <button onClick={() => setEditingBid(false)} disabled={editBidSubmitting}
                    className="text-xs text-gray-600 hover:text-gray-800 px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            ) : undefined}
            onWithdrawBid={myExistingQuote.status === QuoteStatus.Pending ? handleWithdrawBid : undefined}
            withdrawing={withdrawing}
            onToggleMute={isCarrier ? () => muteMutation.mutate(!isMuted) : undefined}
            mutePending={muteMutation.isPending}
            onChatToggle={() => setBidChatOpen(v => !v)}
            chatOpen={bidChatOpen}
          />
          {/* Inline chat panel */}
          {jobId && (
            <div className={bidChatOpen ? 'border-t border-gray-100' : 'hidden'}>
              <MessagingPanel
                jobId={jobId}
                jobReference={job?.referenceNumber ?? ''}
                currentUserId={currentUserId}
                getToken={getToken}
                otherPartyId={job?.customerId ?? ''}
                otherPartyName="Customer"
                enabled={!!account}
              />
            </div>
          )}
        </div>
      )}

      {/* Carrier: Collapsed view of other carriers' quotes (visible only once own bid is placed) */}
      {isBidder && myExistingQuote && (() => {
        const peerQuotes = job.quotes.filter(q => q.carrierId !== currentUserId);
        if (peerQuotes.length === 0) return null;
        const vehicleLabels = ['Car', 'Motorbike', 'Van', 'LGV', 'HGV'];
        return (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-700 text-sm">
                Other Quotes <span className="ml-1 text-gray-400">({peerQuotes.length})</span>
              </h2>
              <span className="text-xs text-gray-400">You cannot view full details of competitor quotes</span>
            </div>
            <div className="divide-y divide-gray-50">
              {peerQuotes.map(q => (
                <div key={q.id} className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{q.carrierName}</p>
                    <p className="text-xs text-gray-400">{vehicleLabels[q.carrierVehicleType] ?? 'Vehicle'}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-bold text-gray-900">£{q.amount.toFixed(2)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      q.status === QuoteStatus.Accepted ? 'bg-green-100 text-green-700'
                      : q.status === QuoteStatus.Rejected ? 'bg-gray-100 text-gray-500'
                      : 'bg-amber-100 text-amber-700'
                    }`}>
                      {q.status === QuoteStatus.Accepted ? 'Accepted'
                        : q.status === QuoteStatus.Rejected ? 'Not selected'
                        : 'Pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/*  Driver: Submit Bid Form  */}
      {canBid && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">Submit Your Bid</h2>

          {bidSuccess && (
            <div className="mb-4 px-3 py-2 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Bid submitted successfully!
            </div>
          )}

          {bidError && (
            <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {bidError}
            </div>
          )}

          <div className="space-y-4">
            {isWorkshop && isRepairJob ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Labour inc. VAT (£)
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      placeholder="Labour amount"
                      value={bidLabour}
                      onChange={e => setBidLabour(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Parts inc. VAT (£)
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      placeholder="Parts amount"
                      value={bidParts}
                      onChange={e => setBidParts(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                </div>
                {bidLabour && bidParts && (
                  <p className="text-xs text-gray-500">
                    Total: <strong className="text-gray-800">£{(parseFloat(bidLabour || '0') + parseFloat(bidParts || '0')).toFixed(2)}</strong>
                    {repairCommissionPercent != null && (
                      <span className="ml-2 text-gray-400">(commission is {repairCommissionPercent}% of labour only)</span>
                    )}
                  </p>
                )}
              </>
            ) : (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Your Price (£)
                </label>
                <input
                  type="number"
                  min={1}
                  step={0.01}
                  placeholder="e.g. 85.00"
                  value={bidAmount}
                  onChange={e => setBidAmount(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Message to Customer <span className="text-gray-300 normal-case">(optional)</span>
              </label>
              <textarea
                rows={3}
                placeholder="Tell the customer about your experience, availability, or any questions."
                value={bidMessage}
                onChange={e => setBidMessage(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
              />
            </div>
          </div>

          <button
            onClick={handleSubmitBid}
            disabled={bidSubmitting || (isWorkshop && isRepairJob ? !bidLabour || !bidParts : !bidAmount)}
            className="mt-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors flex items-center gap-2"
          >
            {bidSubmitting && (
              <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {bidSubmitting ? 'Submitting' : isRepairJob ? 'Submit Quote' : 'Submit Bid'}
          </button>
        </div>
      )}

      {/* Quotes — customers see all bids */}
      {isCustomer && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {isRepairJob
            ? <WorkshopQuotesPanelHeader quoteCount={job.quotes.length} view={quotesView} onToggle={setQuotesView} />
            : (
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
                <h2 className="font-semibold text-gray-900 shrink-0">
                  Quotes ({job.quotes.length})
                </h2>
                {job.quotes.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400 text-xs">↑↓</span>
                    <select
                      value={quoteSort}
                      onChange={e => setQuoteSort(e.target.value as typeof quoteSort)}
                      className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-300 cursor-pointer"
                    >
                      <option value="price-asc">Price: Low → High</option>
                      <option value="price-desc">Price: High → Low</option>
                      <option value="rating-desc">Rating: Best First</option>
                      <option value="jobs-desc">Jobs Completed: Most First</option>
                      <option value="newest">Newest Quote First</option>
                    </select>
                  </div>
                )}
              </div>
            )
          }

          {isRepairJob && quotesView === 'map' && (
            <WorkshopQuoteMapView
              vehicleLat={job.pickupLat}
              vehicleLng={job.pickupLng}
              vehicleAddress={job.pickupAddress}
              quotes={job.quotes}
              onQuoteSelect={(id) => {
                setQuotesView('list');
                setTimeout(() => quoteCardRefs.current[id]?.scrollIntoView({ behavior: 'smooth' }), 50);
              }}
            />
          )}

          {accepted && (
            <div className="mx-5 mt-4 bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  {isCredit
                    ? 'Quote accepted! The driver will be notified. Once they agree, your contract will be issued on credit terms.'
                    : 'Quote accepted! The driver will be notified. Payment will be required once they confirm.'}
                </span>
              </div>
              {showWaitingForDriver && (
                <p className="mt-2 text-amber-700 text-xs flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
                  Waiting for driver to confirm — the Pay Now button will appear here automatically.
                </p>
              )}
              {showCustomerPay && (
                <button
                  onClick={() => navigate(`/payments?jobId=${jobId}`)}
                  className="mt-3 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2 rounded-lg text-sm transition-colors"
                >
                  Pay Now
                </button>
              )}
            </div>
          )}

          {error && (
            <div className="mx-5 mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {job.quotes.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-400 text-sm">
              No quotes yet. Drivers will see your job once it's approved.
            </div>
          ) : (
            <div className="px-4 py-4 space-y-3">
              {[...job.quotes]
                .sort((a, b) => {
                  switch (quoteSort) {
                    case 'price-asc':  return a.amount - b.amount;
                    case 'price-desc': return b.amount - a.amount;
                    case 'rating-desc': return b.averageCarrierRating - a.averageCarrierRating;
                    case 'jobs-desc':  return b.carrierCompletedJobs - a.carrierCompletedJobs;
                    case 'newest':     return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime();
                    default: return 0;
                  }
                })
                .map((q, idx) => (
                  <div key={q.id} ref={el => { quoteCardRefs.current[q.id] = el; }}
                    className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white"
                  >
                    <QuoteCard
                      quote={q}
                      isLowest={quoteSort === 'price-asc' && idx === 0}
                      isCustomer={isCustomer}
                      canAccept={!isAcceptedOrBeyond && !isJobClosed}
                      accepting={accepting === q.id}
                      onAccept={() => handleAcceptQuote(q.id)}
                      onReject={() => handleRejectQuote(q.id)}
                      rejecting={rejecting === q.id}
                      unreadCount={unreadCounts[q.carrierId] ?? 0}
                      jobId={jobId ?? ''}
                      jobReference={job.referenceNumber}
                      currentUserId={currentUserId}
                      getToken={getToken}
                      chatEnabled={!!account && jobId != null && (!isJobClosed || q.status === QuoteStatus.Accepted)}
                    />
                  </div>
                ))
              }
            </div>
          )}
        </div>
      )}

      {/*  Payment & Acceptance Panel  */}
      {isAcceptedOrBeyond && (() => {
        // An internal job reaches this panel with none of the payment flags set - it has no
        // counterparty to pay and no quote to accept - but the assignment, start and
        // complete controls all live in here, so it must render on ownership alone.
        const hasContent = isInternalOwner || showCustomerPay || showWaitingForDriver || showDriverPayFee || showDriverAccept || showDriverAccepted || paymentResult !== null || paymentError !== null;
        if (!hasContent) return null;

        return (
          <div className="mt-6 bg-white border border-gray-200 rounded-xl p-5" data-testid="job-action-panel">
            <h2 className="font-semibold text-gray-900 mb-4">
              {isInternalJob ? 'Fleet Assignment' : 'Payment & Acceptance'}
            </h2>

            {paymentResult === 'success' && (
              <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                <CheckCircle className="w-4 h-4 shrink-0" />
                Payment completed successfully. Your contract will be emailed to both parties shortly.
              </div>
            )}
            {paymentResult === 'cancelled' && (
              <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg text-sm">
                Payment was cancelled. You can try again whenever you're ready.
              </div>
            )}
            {paymentError && (
              <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {paymentError}
              </div>
            )}

            {showWaitingForDriver && (
              <div className="mb-4 flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
                <span className="text-base leading-none mt-0.5"></span>
                <div>
                  <p className="font-semibold">Waiting for driver confirmation</p>
                  <p className="mt-0.5 text-amber-700">
                    The driver has been notified and needs to accept the job. The payment link will appear here once they confirm.
                  </p>
                </div>
              </div>
            )}

            {showCustomerPay && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  {isCredit
                    ? 'Your contract is ready. Please pay the agreed amount to release payment to your carrier.'
                    : job.status >= 5
                      ? 'Your contract is ready. Complete payment to confirm the booking.'
                      : 'The carrier has agreed to your job. Complete payment to generate your contract.'}
                </p>
                {acceptedQuote && (
                  <p className="text-lg font-bold text-gray-900 mb-3">
                    Total: £{acceptedQuote.amount.toFixed(2)}
                  </p>
                )}
                <button
                  onClick={() => navigate(`/payments?jobId=${jobId}`)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors text-sm flex items-center gap-2"
                >
                  Pay Now
                </button>
              </div>
            )}

            {showDriverPayFee && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  Delivery complete. Please pay the platform commission to finalise this job.
                </p>
                {acceptedQuote && (
                  <p className="text-lg font-bold text-gray-900 mb-3">
                    Platform fee: £{acceptedQuote.platformCommission?.toFixed(2) ?? '—'}
                  </p>
                )}
                <button
                  onClick={handleDriverPayFee}
                  disabled={paymentLoading}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors text-sm flex items-center gap-2"
                >
                  {paymentLoading && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {paymentLoading ? 'Redirecting' : 'Pay Platform Fee via Stripe'}
                </button>
              </div>
            )}

            {showDriverAccept && (() => {
              const vehicleTypeLabels = ['Car', 'Motorbike', 'Van', 'LGV', 'HGV'];
              const nonOwnerEmployees = carrierEmployees.filter((e: { isOwner: boolean }) => !e.isOwner);
              const allEmployees      = carrierEmployees;
              return (
                <div className="mb-4 space-y-4">
                  <p className="text-sm text-gray-600">
                    {isWorkshop
                      ? (isCredit
                          ? 'Accept this job. The contract will be issued immediately on credit terms.'
                          : 'Accept this job to allow the customer to proceed with payment.')
                      : (isCredit
                          ? 'Confirm or change the vehicle and driver, then accept. The contract will be issued immediately on credit terms.'
                          : 'Confirm or change the vehicle and driver, then accept to allow the customer to proceed with payment.')}
                  </p>
                  {/* Vehicle selector — Carrier only; Workshop has no fleet */}
                  {!isWorkshop && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Vehicle <span className="text-red-500">*</span>
                    </label>
                    {activeVehicles.length === 0 ? (
                      <p className="text-xs text-red-600">No vehicles in your fleet. Add one in Vehicles before accepting.</p>
                    ) : (
                      <select
                        className="form-input text-sm"
                        value={vehicleIdToUse}
                        onChange={e => setAssignedVehicleId(e.target.value)}
                      >
                        {activeVehicles.map((v: { id: string; registration: string; vehicleType: number; make?: string; model?: string }) => (
                          <option key={v.id} value={v.id}>
                            {v.registration} — {vehicleTypeLabels[v.vehicleType] ?? 'Vehicle'}
                            {v.make ? ` (${v.make}${v.model ? ' ' + v.model : ''})` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  )}
                  {/* Driver selector — shown when there are employees beyond just the owner */}
                  {allEmployees.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Driver
                      </label>
                      <select
                        className="form-input text-sm"
                        value={assignedEmployeeId}
                        onChange={e => setAssignedEmployeeId(e.target.value)}
                      >
                        {allEmployees.map((e: { id: string; fullName: string; isOwner: boolean }) => (
                          <option key={e.id} value={e.id}>
                            {e.fullName}{e.isOwner ? ' (You)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <button
                    onClick={handleDriverAccept}
                    disabled={driverAccepting || (!isWorkshop && !vehicleIdToUse)}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors text-sm flex items-center gap-2"
                  >
                    {driverAccepting && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    {driverAccepting ? 'Accepting…' : 'Accept Job'}
                  </button>
                </div>
              );
            })()}

            {isInternalOwner && (
              <div className="flex items-start gap-2 text-sm text-emerald-700" data-testid="internal-job-notice">
                <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  This is your own fleet job — it was never listed and no other carrier can bid on
                  it. Assign a vehicle and driver below, then start it when they set off.
                </span>
              </div>
            )}

            {!isInternalJob && showDriverAccepted && (
              <div className="flex items-center gap-2 text-sm text-green-700">
                <CheckCircle className="w-4 h-4 shrink-0" />
                {job.carrierAcceptedAt
                  ? `Accepted on ${new Date(job.carrierAcceptedAt).toLocaleString('en-GB')}`
                  : 'You have accepted this job.'}
              </div>
            )}

            {/* Vehicle / Driver assignment — shown after carrier accepts, before job starts */}
            {isCarrierAccepted && (
              <div className="mt-3 border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Vehicle &amp; Driver Assignment</p>
                  {!showReassign && (
                    <button
                      onClick={() => {
                        setReassignVehicleId(currentAssignment?.vehicleId ?? job.assignedVehicleId ?? activeVehicles[0]?.id ?? '');
                        setReassignEmployeeId(currentAssignment?.employeeId ?? job.assignedEmployeeId ?? '');
                        setReassignError(null);
                        setShowReassign(true);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                    >
                      <Pencil className="w-3 h-3" /> Change
                    </button>
                  )}
                </div>

                {!showReassign ? (
                  <div className="text-sm text-gray-700 space-y-1">
                    {currentAssignment?.vehicleRegistration ? (
                      <p><span className="text-gray-500">Vehicle:</span> {currentAssignment.vehicleRegistration}{currentAssignment.vehicleMake ? ` — ${currentAssignment.vehicleMake} ${currentAssignment.vehicleModel ?? ''}` : ''}</p>
                    ) : (
                      <p className="text-gray-400 italic text-xs">No vehicle assigned yet</p>
                    )}
                    {currentAssignment?.employeeName && (
                      <p><span className="text-gray-500">Driver:</span> {currentAssignment.employeeName}</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Vehicle *</label>
                      <select
                        value={reassignVehicleId}
                        onChange={e => setReassignVehicleId(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      >
                        <option value="">— Select vehicle —</option>
                        {activeVehicles.map((v: any) => (
                          <option key={v.id} value={v.id}>{v.registration} — {v.make ?? ''} {v.model ?? ''}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Driver (optional)</label>
                      <select
                        value={reassignEmployeeId}
                        onChange={e => setReassignEmployeeId(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      >
                        <option value="">— You (no employee) —</option>
                        {(carrierEmployees as any[]).map(emp => (
                          <option key={emp.id} value={emp.id}>{emp.fullName}</option>
                        ))}
                      </select>
                    </div>
                    {reassignError && <p className="text-xs text-red-600">{reassignError}</p>}
                    <div className="flex gap-2">
                      <button
                        onClick={() => reassignMutation.mutate({ vehicleId: reassignVehicleId, employeeId: reassignEmployeeId || undefined })}
                        disabled={!reassignVehicleId || reassignMutation.isPending}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold py-2 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                      >
                        {reassignMutation.isPending && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                        {reassignMutation.isPending ? 'Saving…' : 'Save Assignment'}
                      </button>
                      <button
                        onClick={() => { setShowReassign(false); setReassignError(null); }}
                        className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Start Job — shown to the assigned carrier once the contract is generated */}
            {isAssignedDriver && (job.status === JobStatus.ContractGenerated || job.status === JobStatus.CarrierAccepted) && (
              <div className="mt-3">
                {jobActionError && (
                  <p className="text-xs text-red-600 mb-2">{jobActionError}</p>
                )}
                <button
                  onClick={handleStartJob}
                  disabled={jobActionBusy}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors text-sm flex items-center gap-2"
                >
                  {jobActionBusy && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {jobActionBusy ? 'Starting…' : 'Start Job'}
                </button>
              </div>
            )}

            {/* Paused indicator — shown to customer when job is paused */}
            {isCustomer && (job.status as number) === 14 && (
              <div className="mt-3 flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
                <span className="text-base leading-none mt-0.5"></span>
                <div>
                  <p className="font-semibold">Delivery paused</p>
                  <p className="mt-0.5 text-amber-700">Your carrier has temporarily paused this job. GPS tracking is off. They will resume shortly.</p>
                </div>
              </div>
            )}

            {/* Resume Job — shown to assigned carrier when job is Paused */}
            {isAssignedDriver && (job.status as number) === 14 && (
              <div className="mt-3">
                {jobActionError && <p className="text-xs text-red-600 mb-2">{jobActionError}</p>}
                <button
                  onClick={handleResumeJob}
                  disabled={jobActionBusy}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors text-sm flex items-center gap-2"
                >
                  {jobActionBusy && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {jobActionBusy ? 'Resuming…' : 'Resume Job'}
                </button>
              </div>
            )}

            {/* Mark as Delivered + Pause — shown when job is In Progress */}
            {isAssignedDriver && job.status === JobStatus.InProgress && (
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                {jobActionError && (
                  <p className="w-full text-xs text-red-600">{jobActionError}</p>
                )}
                <button
                  onClick={handleCompleteJob}
                  disabled={jobActionBusy}
                  className="bg-amber-500 hover:bg-amber-600 disabled:bg-amber-400 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors text-sm flex items-center gap-2"
                >
                  {jobActionBusy && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {jobActionBusy ? 'Updating…' : isRepairJob ? 'Complete Job' : 'Mark as Delivered'}
                </button>
                <button
                  onClick={handlePauseJob}
                  disabled={jobActionBusy}
                  className="border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-gray-600 font-semibold px-5 py-2.5 rounded-lg transition-colors text-sm flex items-center gap-2"
                >
                   Pause Job
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* Chat  drivers/workshops/hauliers:
            ¢ open bidding phase (status 2-3): any driver can ask questions
            ¢ confirmed phase (status 4+): assigned driver only            */}
      {/* Standalone chat shown only when driver hasn't placed a bid yet; once bid exists, chat lives inside the bid card */}
      {driverCanChat && !myExistingQuote && jobId && (
        <div className="mt-6">
          <MessagingPanel
            jobId={jobId}
            jobReference={job.referenceNumber}
            currentUserId={currentUserId}
            getToken={getToken}
            otherPartyId={job.customerId}
            otherPartyName="Customer"
            enabled={!!account}
          />
        </div>
      )}

      {/* POC submission — shown to the assigned carrier/workshop while the job is InProgress/Paused and
          no POC yet. Multi-pickup-drop jobs capture proof of collection per-stop instead (Collect button). */}
      {(job.status === JobStatus.InProgress || job.status === JobStatus.Paused)
        && job.jobType !== JobType.MultiPickupDropDelivery
        && !job.pocCapturedAt && !pocSubmitted && jobId && (isAssignedDriver || isWorkshop) && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-5">
          <h2 className="font-semibold text-blue-800 mb-2">Proof of Collection</h2>
          <p className="text-sm text-blue-700 mb-4">
            Submit collection photos, sender signature, and GPS location to confirm you have collected the goods.
          </p>
          <button
            onClick={() => setShowPocModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
          >
            Submit Proof of Collection
          </button>
        </div>
      )}

      {(pocSubmitted || !!job.pocCapturedAt) && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-blue-600 shrink-0" />
            <p className="text-sm text-blue-700 font-medium">
              Proof of collection submitted successfully.
            </p>
          </div>

          {(job.pocPhotoUrls?.length || job.pocSignatureUrl || job.pocSenderName) && (
            <div className="mt-4 pt-4 border-t border-blue-200">
              {job.pocSenderName && (
                <p className="text-xs text-blue-800 mb-3">
                  <span className="font-semibold">Collected from:</span> {job.pocSenderName}
                </p>
              )}
              {job.pocPhotoUrls && job.pocPhotoUrls.length > 0 && (
                <>
                  <p className="text-xs font-medium text-blue-700 uppercase tracking-wide mb-2">Collection Photos / Videos</p>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {job.pocPhotoUrls.map((url, i) => {
                      const isVideo = /\.(mp4|mov|webm|avi)(\?|$)/i.test(url);
                      return isVideo ? (
                        <video
                          key={i}
                          src={url}
                          controls
                          className="w-full rounded-lg border border-blue-200 bg-black"
                          style={{ aspectRatio: '4/3', objectFit: 'cover' }}
                        />
                      ) : (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                          <img
                            src={url}
                            alt={`Proof of collection ${i + 1}`}
                            className="w-full rounded-lg border border-blue-200 object-cover hover:opacity-90 transition-opacity cursor-zoom-in"
                            style={{ aspectRatio: '4/3' }}
                          />
                        </a>
                      );
                    })}
                  </div>
                </>
              )}
              {job.pocSignatureUrl && (
                <>
                  <p className="text-xs font-medium text-blue-700 uppercase tracking-wide mb-2">Sender Signature</p>
                  <a href={job.pocSignatureUrl} target="_blank" rel="noopener noreferrer">
                    <img
                      src={job.pocSignatureUrl}
                      alt="Sender signature"
                      className="h-24 rounded-lg border border-blue-200 bg-white object-contain cursor-zoom-in"
                    />
                  </a>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* POC modal — job-level collection */}
      {showPocModal && jobId && (
        <PocSubmissionModal
          jobId={jobId}
          getToken={getToken}
          onClose={() => setShowPocModal(false)}
          onSuccess={() => { setShowPocModal(false); setPocSubmitted(true); }}
        />
      )}

      {/* POC modal — per-pickup collection on a multi-pickup-drop job */}
      {collectingPickupId && jobId && (
        <PocSubmissionModal
          jobId={jobId}
          pickupId={collectingPickupId}
          getToken={getToken}
          onClose={() => setCollectingPickupId(null)}
          onSuccess={refreshAfterPickupCollected}
        />
      )}

      {/* Delivery OTP — shown to customer while InProgress or Completed (awaiting POD); hidden once POD is submitted */}
      {isCustomer && (job.status === JobStatus.InProgress || job.status === JobStatus.Completed) && !podSubmitted && !job.podCapturedAt && jobId && (
        <DeliveryOtpPanel jobId={jobId} />
      )}

      {/* POD submission — shown only to the assigned carrier/workshop when job is Completed and no POD yet */}
      {job.status === JobStatus.Completed && !job.podCapturedAt && !podSubmitted && jobId && (isAssignedDriver || isWorkshop) && (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-5">
          <h2 className="font-semibold text-amber-800 mb-2">Proof of Delivery Required</h2>
          <p className="text-sm text-amber-700 mb-4">
            {isRepairJob
              ? 'The repair is marked complete. Submit delivery photos and customer signature to trigger payment release.'
              : 'The job is marked complete. Submit delivery photos, customer signature, and GPS location to trigger payment release.'}
          </p>
          <button
            onClick={() => setShowPodModal(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
          >
            Submit Proof of Delivery
          </button>
        </div>
      )}

      {(podSubmitted || !!job.podCapturedAt) && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
            <p className="text-sm text-green-700 font-medium">
              Proof of Delivery submitted successfully. Payment will be released within 24-48 hours once verified.
            </p>
          </div>

          {(job.podPhotoUrls?.length || job.podSignatureUrl || job.podRecipientName) && (
            <div className="mt-4 pt-4 border-t border-green-200">
              {job.podRecipientName && (
                <p className="text-xs text-green-800 mb-3">
                  <span className="font-semibold">Received by:</span> {job.podRecipientName}
                </p>
              )}
              {job.podPhotoUrls && job.podPhotoUrls.length > 0 && (
                <>
                  <p className="text-xs font-medium text-green-700 uppercase tracking-wide mb-2">Delivery Photos / Videos</p>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {job.podPhotoUrls.map((url, i) => {
                      const isVideo = /\.(mp4|mov|webm|avi)(\?|$)/i.test(url);
                      return isVideo ? (
                        <video
                          key={i}
                          src={url}
                          controls
                          className="w-full rounded-lg border border-green-200 bg-black"
                          style={{ aspectRatio: '4/3', objectFit: 'cover' }}
                        />
                      ) : (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                          <img
                            src={url}
                            alt={`Proof of delivery ${i + 1}`}
                            className="w-full rounded-lg border border-green-200 object-cover hover:opacity-90 transition-opacity cursor-zoom-in"
                            style={{ aspectRatio: '4/3' }}
                          />
                        </a>
                      );
                    })}
                  </div>
                </>
              )}
              {job.podSignatureUrl && (
                <>
                  <p className="text-xs font-medium text-green-700 uppercase tracking-wide mb-2">Recipient Signature</p>
                  <a href={job.podSignatureUrl} target="_blank" rel="noopener noreferrer">
                    <img
                      src={job.podSignatureUrl}
                      alt="Recipient signature"
                      className="h-24 rounded-lg border border-green-200 bg-white object-contain cursor-zoom-in"
                    />
                  </a>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* POD modal */}
      {showPodModal && jobId && (
        <PodSubmissionModal
          jobId={jobId}
          getToken={getToken}
          isRepairJob={isRepairJob}
          onClose={() => setShowPodModal(false)}
          onSuccess={() => { setShowPodModal(false); setPodSubmitted(true); }}
        />
      )}

      {/* Review section — available to both parties from the moment a quote is accepted */}
      {jobId && reviewCounterpartyId && (
        <ReviewsPanel
          jobId={jobId}
          currentUserId={currentUserId}
          getToken={getToken}
          canSubmit={canReview}
          counterpartyId={reviewCounterpartyId}
          counterpartyName={reviewCounterpartyName}
          revieweeLabel={reviewRevieweeLabel}
        />
      )}

      {/* Raise Dispute — customer on completed jobs */}
      {isCustomer && [7, 8, 9, 10].includes(job.status) && !disputeSubmitted && (
        <div className="mt-6 bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="font-semibold text-gray-900 mb-2">Issue with this job?</h2>
          <p className="text-sm text-gray-500 mb-3">
            If you experienced a problem with this delivery, you can raise a formal dispute for review.
          </p>
          <button
            onClick={() => setShowDisputeModal(true)}
            className="border border-red-300 text-red-600 hover:bg-red-50 font-semibold px-5 py-2 rounded-lg text-sm transition-colors"
          >
            Raise Dispute
          </button>
        </div>
      )}
      {disputeSubmitted && (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-700 font-medium">Dispute raised and submitted for review.</p>
        </div>
      )}

      {/* Dispute modal */}
      {showDisputeModal && jobId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h2 className="font-semibold text-gray-900 text-lg">Open Dispute</h2>
              <button onClick={() => setShowDisputeModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {disputeError && (
                <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{disputeError}</div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Reason / Type</label>
                <select
                  value={disputeCategory}
                  onChange={e => setDisputeCategory(Number(e.target.value) as DisputeCategory)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value={DisputeCategory.ItemDamaged}>Item Damaged</option>
                  <option value={DisputeCategory.ItemMissing}>Item Missing</option>
                  <option value={DisputeCategory.LateDelivery}>Late Delivery</option>
                  <option value={DisputeCategory.CarrierNoShow}>Carrier No-Show</option>
                  <option value={DisputeCategory.IncorrectDelivery}>Incorrect Delivery</option>
                  <option value={DisputeCategory.PaymentDisputed}>Payment Disputed</option>
                  <option value={DisputeCategory.FraudSuspected}>Fraud Suspected</option>
                  <option value={DisputeCategory.VehicleRepairQuality}>Repair Quality</option>
                  <option value={DisputeCategory.RelayHandoverFailed}>Relay Handover Failed</option>
                  <option value={DisputeCategory.Other}>Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Description</label>
                <textarea
                  rows={4}
                  placeholder="Describe the issue in detail. Include any relevant dates and amounts."
                  value={disputeDescription}
                  onChange={e => setDisputeDescription(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Evidence (optional)</label>
                <input type="file" accept="image/*,.pdf" className="text-sm text-gray-600" />
              </div>
              <button
                onClick={async () => {
                  setDisputeSubmitting(true);
                  setDisputeError(null);
                  try {
                    const client = createApiClient(getToken);
                    await client.post('/api/disputes', {
                      jobId,
                      raisedByUserId: currentUserId || '00000000-0000-0000-0000-000000000000',
                      raisedByParty: DisputeParty.Customer,
                      category: disputeCategory,
                      title: disputeTitle || `Dispute for job ${job.referenceNumber}`,
                      description: disputeDescription,
                    });
                    setShowDisputeModal(false);
                    setDisputeSubmitted(true);
                  } catch (e: any) {
                    setDisputeError(e.response?.data?.detail ?? e.response?.data?.error ?? e.message ?? 'Failed to open dispute.');
                  } finally {
                    setDisputeSubmitting(false);
                  }
                }}
                disabled={disputeSubmitting}
                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
              >
                {disputeSubmitting ? 'Submitting…' : 'Open Dispute'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer: accept or reject a proposed resolution */}
      {isCustomer && jobDispute && jobDispute.status === DisputeStatus.Proposed && (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-5">
          <h2 className="font-semibold text-amber-900 mb-1">Dispute Resolution Proposed</h2>
          <p className="text-sm text-amber-800 mb-1">
            <strong>Ref:</strong> {jobDispute.referenceNumber}
          </p>
          {jobDispute.resolutionNotes && (
            <p className="text-sm text-amber-800 mb-3">{jobDispute.resolutionNotes}</p>
          )}
          {jobDispute.refundAmount != null && (
            <p className="text-sm font-semibold text-amber-900 mb-3">
              Proposed refund: £{jobDispute.refundAmount.toFixed(2)}
            </p>
          )}
          <div className="flex gap-3">
            <button
              onClick={async () => {
                const client = createApiClient(getToken);
                await client.post(`/api/disputes/${jobDispute.id}/respond-resolution`, { accepted: true });
                refetchDispute();
              }}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2 rounded-lg text-sm"
            >
              Accept Resolution
            </button>
            <button
              onClick={async () => {
                const client = createApiClient(getToken);
                await client.post(`/api/disputes/${jobDispute.id}/respond-resolution`, { accepted: false });
                refetchDispute();
              }}
              className="border border-red-400 text-red-600 hover:bg-red-50 font-semibold px-5 py-2 rounded-lg text-sm"
            >
              Reject Resolution
            </button>
          </div>
        </div>
      )}

      {/* Customer: dispute resolved confirmation */}
      {isCustomer && jobDispute && jobDispute.status === DisputeStatus.Resolved && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
          <p className="text-sm text-green-800 font-medium">
            Dispute resolved.
            {jobDispute.refundAmount != null && ` Refund of £${jobDispute.refundAmount.toFixed(2)} approved.`}
          </p>
        </div>
      )}

      {/* Admin: dispute management panel */}
      {isAdmin && jobDispute && [DisputeStatus.Open, DisputeStatus.InReview, DisputeStatus.Proposed].includes(jobDispute.status) && (
        <div className="mt-6 bg-white border border-blue-200 rounded-xl p-5">
          <h2 className="font-semibold text-gray-900 mb-1">Dispute Management</h2>
          <p className="text-xs text-gray-500 mb-3">
            Ref: <span className="font-mono">{jobDispute.referenceNumber}</span>
            {' · '}Status: <strong>{jobDispute.status === DisputeStatus.Open ? 'Open' : jobDispute.status === DisputeStatus.InReview ? 'In Review' : 'Resolution Proposed'}</strong>
          </p>
          <p className="text-sm text-gray-700 mb-4">{jobDispute.description}</p>

          {adminDisputeError && (
            <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{adminDisputeError}</div>
          )}

          {/* Assign if Open */}
          {jobDispute.status === DisputeStatus.Open && (
            <button
              onClick={async () => {
                setAdminDisputeSubmitting(true); setAdminDisputeError(null);
                try {
                  const client = createApiClient(getToken);
                  await client.post(`/api/disputes/${jobDispute.id}/assign`, { agentId: currentUserId });
                  refetchDispute();
                } catch (e: any) {
                  setAdminDisputeError(e.response?.data?.detail ?? e.message ?? 'Failed to assign dispute.');
                } finally { setAdminDisputeSubmitting(false); }
              }}
              disabled={adminDisputeSubmitting}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold px-5 py-2 rounded-lg text-sm"
            >
              {adminDisputeSubmitting ? 'Assigning…' : 'Assign to Self (Mark In Review)'}
            </button>
          )}

          {/* Propose / re-propose resolution if InReview or Proposed */}
          {(jobDispute.status === DisputeStatus.InReview || jobDispute.status === DisputeStatus.Proposed) && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Resolution Type</label>
                <select
                  value={adminResolutionType}
                  onChange={e => setAdminResolutionType(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value={DisputeResolutionType.PartialRefundToCustomer}>PartialRefund</option>
                  <option value={DisputeResolutionType.FullRefundToCustomer}>FullRefund</option>
                  <option value={DisputeResolutionType.NoRefund}>NoRefund</option>
                  <option value={DisputeResolutionType.CarrierPenalty}>CarrierPenalty</option>
                  <option value={DisputeResolutionType.MutualAgreement}>MutualAgreement</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Refund Amount (£)</label>
                <input
                  type="number"
                  placeholder="amount"
                  value={adminRefundAmount}
                  onChange={e => setAdminRefundAmount(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Resolution Notes</label>
                <textarea
                  rows={3}
                  value={adminResolutionNotes}
                  onChange={e => setAdminResolutionNotes(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                  placeholder="Explain the resolution to the customer"
                />
              </div>
              <button
                onClick={async () => {
                  setAdminDisputeSubmitting(true); setAdminDisputeError(null);
                  try {
                    const client = createApiClient(getToken);
                    await client.post(`/api/disputes/${jobDispute.id}/propose-resolution`, {
                      resolutionType: adminResolutionType,
                      refundAmount: adminRefundAmount ? parseFloat(adminRefundAmount) : undefined,
                      explanation: adminResolutionNotes,
                    });
                    setAdminRefundAmount(''); setAdminResolutionNotes('');
                    refetchDispute();
                  } catch (e: any) {
                    setAdminDisputeError(e.response?.data?.detail ?? e.message ?? 'Failed to propose resolution.');
                  } finally { setAdminDisputeSubmitting(false); }
                }}
                disabled={adminDisputeSubmitting}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white font-semibold px-5 py-2 rounded-lg text-sm"
              >
                {adminDisputeSubmitting ? 'Proposing…' : jobDispute.status === DisputeStatus.Proposed ? 'Update Resolution Proposal' : 'Propose Resolution'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Contract section — visible only to the two contracting parties (API enforces this; not shown to Admin/Ops or via the Employee Portal) */}
      {contract && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <h2 className="font-semibold text-green-800">Contract Generated</h2>
          </div>
          <p className="text-sm text-green-700 mb-3">
            Reference: <span className="font-mono font-medium">{contract.contractReference}</span>
          </p>
          <div className="flex gap-3 text-sm flex-wrap">
            <span className="text-green-700">Agreed: <strong>£{contract.agreedPrice.toFixed(2)}</strong></span>
            <span className="text-gray-400">|</span>
            <span className="text-gray-600">Platform Fee: £{contract.commissionAmount.toFixed(2)}</span>
            <span className="text-gray-400">|</span>
            <span className="text-gray-600">
              Cancel by: {new Date(contract.cancellationDeadline).toLocaleString('en-GB')}
            </span>
            {contract.lastRegeneratedAt && (
              <>
                <span className="text-gray-400">|</span>
                <span className="text-amber-700">
                  Updated {new Date(contract.lastRegeneratedAt).toLocaleString('en-GB')} (vehicle/driver reassigned)
                </span>
              </>
            )}
          </div>
          <button
            onClick={handleDownloadContract}
            disabled={downloadingContract}
            className="inline-flex items-center gap-1 mt-3 text-sm text-green-700 hover:text-green-900 font-medium disabled:opacity-50"
          >
            {downloadingContract ? 'Opening…' : 'Download Contract PDF'} <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

//
//  QUOTE CARD COMPONENT
//
function timeAgo(iso: string): string {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function QuoteCard({
  quote, isLowest, isCustomer, canAccept, accepting, onAccept,
  onReject, rejecting, unreadCount = 0,
  jobId, jobReference, currentUserId, getToken, chatEnabled,
  // Carrier "my bid" mode
  isMyBid = false, isMuted = false, onEditBid, editingBid = false,
  editBidContent, onWithdrawBid, withdrawing = false, onToggleMute, mutePending = false,
  onChatToggle, chatOpen = false,
}: {
  quote: QuoteSummaryDto;
  isLowest: boolean;
  isCustomer: boolean;
  canAccept: boolean;
  accepting: boolean;
  onAccept: () => void;
  onReject?: () => void;
  rejecting?: boolean;
  unreadCount?: number;
  jobId: string;
  jobReference: string;
  currentUserId: string;
  getToken: () => Promise<string>;
  chatEnabled: boolean;
  // Carrier "my bid" mode
  isMyBid?: boolean;
  isMuted?: boolean;
  onEditBid?: () => void;
  editingBid?: boolean;
  editBidContent?: ReactNode;
  onWithdrawBid?: () => void;
  withdrawing?: boolean;
  onToggleMute?: () => void;
  mutePending?: boolean;
  onChatToggle?: () => void;
  chatOpen?: boolean;
}) {
  const [expanded, setExpanded] = useState(isCustomer || isMyBid);
  const [favourite, setFavourite] = useState(false);
  const displayName = quote.carrierName || 'Carrier';
  const initials = displayName.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();
  const isPending = quote.status === QuoteStatus.Pending;
  const isAccepted = quote.status === QuoteStatus.Accepted;
  const isDeclined = quote.status === QuoteStatus.Rejected;
  const commissionPct = quote.platformCommission != null && quote.amount > 0
    ? Math.round((quote.platformCommission / quote.amount) * 100)
    : 15;

  return (
    <div className="bg-white">
      {/* ── Header row — always visible ── */}
      <div
        className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors select-none"
        onClick={() => setExpanded(v => !v)}
      >
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm shrink-0">
          {initials}
        </div>

        {/* Favourite */}
        <button
          onClick={e => { e.stopPropagation(); setFavourite(v => !v); }}
          title={favourite ? 'Remove from favourites' : 'Add to favourites'}
          className="shrink-0 p-1 -ml-1"
        >
          <Heart className={`w-4 h-4 transition-colors ${favourite ? 'fill-pink-500 text-pink-500' : 'text-gray-300 hover:text-pink-400'}`} />
        </button>

        {/* Name + status + rating row */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 text-sm">{isMyBid ? 'My Bid' : displayName}</span>
            {isMyBid && <span className="text-xs font-semibold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">Mine</span>}
            {isAccepted && <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">✓ Accepted</span>}
            {isDeclined && <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Not selected</span>}
            {isLowest && isPending && !isMyBid && <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Lowest</span>}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
            <span className="flex items-center gap-0.5">
              <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
              {quote.averageCarrierRating.toFixed(1)}
            </span>
            <span>·</span>
            <span>{quote.carrierCompletedJobs} jobs</span>
            <span>·</span>
            <span>{timeAgo(quote.submittedAt)}</span>
          </div>
        </div>

        {/* Price + net + icons */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-right">
            <p className="text-base font-bold text-gray-900">£{quote.amount.toFixed(2)}</p>
            {!isCustomer && quote.carrierNetAmount != null && (
              <p className="text-xs text-gray-400">net £{quote.carrierNetAmount.toFixed(2)}</p>
            )}
          </div>

          {/* Customer: Accept / Reject icons */}
          {isCustomer && canAccept && isPending && (
            <>
              <button
                onClick={e => { e.stopPropagation(); onAccept(); }}
                disabled={accepting}
                title="Accept quote"
                className="p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-colors disabled:opacity-50"
              >
                <Check className="w-4 h-4 text-emerald-600" />
              </button>
              {onReject && (
                <button
                  onClick={e => { e.stopPropagation(); onReject(); }}
                  disabled={rejecting}
                  title="Decline quote"
                  className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 border border-red-200 transition-colors disabled:opacity-50"
                >
                  <X className="w-4 h-4 text-red-500" />
                </button>
              )}
            </>
          )}

          {/* Carrier "my bid": Edit + Mute in header */}
          {isMyBid && isPending && (
            <>
              {onEditBid && !editingBid && (
                <button
                  onClick={e => { e.stopPropagation(); onEditBid(); }}
                  title="Edit bid"
                  className="p-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5 text-gray-600" />
                </button>
              )}
              {onToggleMute && (
                <button
                  onClick={e => { e.stopPropagation(); onToggleMute(); }}
                  disabled={mutePending}
                  title={isMuted ? 'Unmute notifications' : 'Mute notifications'}
                  className={`p-1.5 rounded-lg border transition-colors disabled:opacity-50 ${
                    isMuted ? 'bg-white border-gray-200 text-gray-400' : 'bg-indigo-50 border-indigo-200 text-indigo-600 hover:bg-indigo-100'
                  }`}
                >
                  {isMuted ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
                </button>
              )}
            </>
          )}

          {expanded
            ? <ChevronUp className="w-4 h-4 text-gray-400 ml-1" />
            : <ChevronDown className="w-4 h-4 text-gray-400 ml-1" />
          }
        </div>
      </div>

      {/* ── Expanded detail ── */}
      {expanded && (
        <div className="border-t border-gray-100">

          {/* Info grid */}
          <div className="px-4 pt-4 pb-3 grid grid-cols-2 gap-2">
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <p className="text-xs text-gray-400 mb-0.5">Quote amount</p>
              <p className="text-sm font-semibold text-gray-900">£{quote.amount.toFixed(2)}</p>
            </div>
            {!isCustomer && (
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-400 mb-0.5">Platform fee ({commissionPct}%)</p>
                <p className="text-sm font-semibold text-gray-900">
                  {quote.platformCommission != null ? `£${quote.platformCommission.toFixed(2)}` : '—'}
                </p>
              </div>
            )}
            {!isCustomer && (
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-400 mb-0.5">Carrier receives</p>
                <p className="text-sm font-semibold text-emerald-700">
                  {quote.carrierNetAmount != null ? `£${quote.carrierNetAmount.toFixed(2)}` : '—'}
                </p>
              </div>
            )}
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <p className="text-xs text-gray-400 mb-0.5">Quote expires</p>
              <p className="text-sm font-semibold text-gray-900">
                {new Date(quote.expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </p>
            </div>
          </div>

          {/* Accept / Decline full buttons — customer only */}
          {isCustomer && canAccept && isPending && (
            <div className="px-4 pb-3 flex gap-2">
              <button
                onClick={onAccept}
                disabled={accepting}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
              >
                {accepting ? 'Accepting...' : 'Accept Quote'}
              </button>
              {onReject && (
                <button
                  onClick={onReject}
                  disabled={rejecting}
                  className="flex-1 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-gray-600 text-sm font-medium py-2 rounded-lg transition-colors"
                >
                  {rejecting ? 'Declining...' : 'Decline'}
                </button>
              )}
            </div>
          )}

          {/* Carrier "my bid" edit form */}
          {isMyBid && editingBid && editBidContent && (
            <div className="px-4 pb-4">{editBidContent}</div>
          )}

          {/* Carrier "my bid" actions — Chat + Withdraw */}
          {isMyBid && !editingBid && (isPending || isAccepted) && (
            <div className="px-4 pb-3 flex gap-2">
              <button
                onClick={onChatToggle}
                className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-colors ${
                  chatOpen ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <MessageCircle className="w-3.5 h-3.5" /> {chatOpen ? 'Close Chat' : 'Chat with Customer'}
              </button>
              {isPending && onWithdrawBid && (
                <button
                  onClick={onWithdrawBid}
                  disabled={withdrawing}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-60"
                >
                  {withdrawing
                    ? <span className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                    : <X className="w-3.5 h-3.5" />}
                  {withdrawing ? 'Withdrawing…' : 'Withdraw Bid'}
                </button>
              )}
            </div>
          )}

          {/* Inline chat — always visible when expanded */}
          {chatEnabled && jobId && (
            <div className="border-t border-gray-100">
              <div className="px-4 py-2 flex items-center gap-2">
                <MessageCircle className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-xs font-medium text-gray-600">Chat with {displayName}</span>
                {unreadCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
                <span className="ml-auto w-2 h-2 rounded-full bg-emerald-400" title="Online" />
              </div>
              <div className="px-4 pb-4">
                <MessagingPanel
                  jobId={jobId}
                  jobReference={jobReference}
                  currentUserId={currentUserId}
                  getToken={getToken}
                  otherPartyId={quote.carrierId}
                  otherPartyName={displayName}
                  enabled={chatEnabled}
                  initialMessage={quote.message || undefined}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


